/**
 * iOS login keyboard.
 *
 * Two systems were both trying to dodge the keyboard, and they stacked:
 * Capacitor shrinks the WebView (after a delay, in one jump), and a second
 * inset on the login screen then reserved that height again. The card was
 * pushed into a short band at the top and the rest of the screen went blank.
 * The shortened WebView also leaves the window color in the keyboard's
 * rounded corners.
 *
 * On the auth screen the WebView stays full screen, the page keeps painting
 * behind the keyboard, and only the card moves — over the same curve as the
 * keyboard, starting when `keyboardWillShow` fires.
 */
import type { PluginListenerHandle } from '@capacitor/core'
import { isIosNative, isNativePlatform } from '@/utils/platform'
import { noteNativeResizeMode } from '@/utils/nativeResizeMode'

const VIEWPORT_OVERLAY = 'interactive-widget=overlays-content'

let authKeyboardActive = false
/** Mobile assistant chat is open (see nativeChatKeyboard.ts). */
let chatKeyboardActive = false
/** A bottom sheet that rides the keyboard itself is open (see nativeSheetKeyboard.ts). */
let sheetKeyboardActive = false
let applyQueue: Promise<void> = Promise.resolve()
let listeners: PluginListenerHandle[] = []
let startToken = 0
let screenEl: HTMLElement | null = null
let prevHtmlBg = ''
let prevBodyBg = ''
let savedCanvas = false
let previousViewport: string | null = null
let baseInnerHeight = 0
let lastKeyboardHeight = 0
let caretHoldTimer = 0

/** Matches the `top` transition on `.native-auth-stage`. */
const SLIDE_MS = 250

if (typeof window !== 'undefined' && isIosNative() && isNativeLoginPath(window.location.pathname)) {
    authKeyboardActive = true
}

function isNativeLoginPath(pathname: string) {
    const path = pathname.replace(/\/$/, '') || '/'
    return path === '/native-login'
}

function stageEl() {
    return screenEl?.querySelector<HTMLElement>('.native-auth-stage') ?? null
}

function noteBaseHeight() {
    baseInnerHeight = Math.max(baseInnerHeight, window.innerHeight)
}

/** True once Capacitor has already shortened the WebView for the keyboard. */
function webViewAlreadyShrunk() {
    noteBaseHeight()
    return baseInnerHeight - window.innerHeight > 140
}

function paintCanvas(screen: HTMLElement | null) {
    if (!savedCanvas) {
        prevHtmlBg = document.documentElement.style.backgroundColor
        prevBodyBg = document.body.style.backgroundColor
        savedCanvas = true
    }
    if (!screen) return
    const color = getComputedStyle(screen).backgroundColor
    if (!color || color === 'rgba(0, 0, 0, 0)' || color === 'transparent') return
    // !important in CSS beats later theme inline styles, so the color behind
    // the keyboard stays the login canvas instead of flashing back to white.
    document.documentElement.style.setProperty('--native-auth-canvas', color)
    document.documentElement.classList.add('native-auth-keyboard')
    document.documentElement.style.backgroundColor = color
    document.body.style.backgroundColor = color
}

function restoreCanvas() {
    document.documentElement.classList.remove('native-auth-keyboard')
    document.documentElement.style.removeProperty('--native-auth-canvas')
    if (savedCanvas) {
        document.documentElement.style.backgroundColor = prevHtmlBg
        document.body.style.backgroundColor = prevBodyBg
        savedCanvas = false
    }
}

/**
 * Ask WebKit not to resize the layout viewport when the keyboard opens, so
 * `position: fixed` stays full screen and the page still paints into the
 * keyboard's rounded corners.
 */
export function overlayViewport() {
    const meta = document.querySelector('meta[name="viewport"]')
    if (!meta) return
    const content = meta.getAttribute('content') || ''
    if (content.includes('interactive-widget=')) return
    previousViewport = content
    meta.setAttribute('content', `${content}, ${VIEWPORT_OVERLAY}`)
}

export function restoreViewport() {
    // An open keyboard-riding sheet still needs the overlay (chat or auth going away under it).
    if (sheetKeyboardActive) return
    const meta = document.querySelector('meta[name="viewport"]')
    if (!meta || previousViewport == null) return
    meta.setAttribute('content', previousViewport)
    previousViewport = null
}

/**
 * iOS draws the caret in layout coordinates and only refreshes it on the
 * next blink. A transform moves the field without the caret, so it flashes
 * at the old spot. `top` keeps the caret inside the field; hiding it for
 * the slide covers the one blink that still lands on the previous position.
 */
function holdCaretDuringSlide() {
    document.documentElement.classList.add('native-auth-caret-hold')
    window.clearTimeout(caretHoldTimer)
    caretHoldTimer = window.setTimeout(() => {
        document.documentElement.classList.remove('native-auth-caret-hold')
    }, SLIDE_MS + 40)
}

function applyShift(reportedHeight: number) {
    const stage = stageEl()
    if (!stage) return
    // The WebView already moved. A second shift is what left the blank band.
    if (webViewAlreadyShrunk()) {
        stage.style.top = '0px'
        stage.style.transform = ''
        stage.style.height = ''
        stage.style.minHeight = ''
        document.documentElement.classList.remove('native-auth-caret-hold')
        return
    }
    // Pin the stage to the pre-keyboard height so 100dvh shrinking doesn't
    // move the card a second time.
    if (reportedHeight > 0 && baseInnerHeight > 0) {
        stage.style.height = `${baseInnerHeight}px`
        stage.style.minHeight = `${baseInnerHeight}px`
    }
    const shift = reportedHeight > 0 ? Math.round(reportedHeight / 2) : 0
    const nextTop = `${-shift}px`
    if (stage.style.top !== nextTop) holdCaretDuringSlide()
    stage.style.transform = ''
    stage.style.top = nextTop
}

function onViewportChange() {
    noteBaseHeight()
    if (!authKeyboardActive) return
    applyShift(lastKeyboardHeight)
}

/**
 * The WebView draws under the iOS status bar (no solid reserved band).
 * Login and the logged-in shell both pad with safe-area-inset-top.
 */
async function setStatusBarOverlay() {
    const { StatusBar, Style } = await import('@capacitor/status-bar')
    const dark = document.documentElement.classList.contains('dark')
    await StatusBar.setOverlaysWebView({ overlay: true })
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light })
}

/**
 * Chat keeps the WebView full height while the keyboard is up so the chat
 * dock can ride the keyboard's own animation (see nativeChatKeyboard.ts)
 * instead of waiting for Capacitor's one-jump resize. The flag flips
 * synchronously; the plugin calls are queued.
 */
export function setChatKeyboardActive(active: boolean): Promise<void> {
    chatKeyboardActive = active
    return applyNativeKeyboardMode()
}

/**
 * A bottom sheet with its own field (the New pitch composer) lifts itself
 * with the keyboard, the same way the chat dock does, so the WebView stays
 * full height while it's open. Resize None is noted synchronously: the
 * sheet's field is focused right after, and the shared keyboard inset
 * (native/keyboard.ts) must already read the keyboard as an inset.
 */
export function setSheetKeyboardActive(active: boolean): Promise<void> {
    if (sheetKeyboardActive === active) return applyQueue
    sheetKeyboardActive = active
    if (active) {
        noteNativeResizeMode('none')
        overlayViewport()
    } else if (!chatKeyboardActive && !authKeyboardActive) {
        restoreViewport()
    }
    return applyNativeKeyboardMode()
}

/** Last call wins. Reads `authKeyboardActive` when the job runs. */
export function applyNativeKeyboardMode(): Promise<void> {
    if (!isNativePlatform()) return Promise.resolve()

    const job = applyQueue.then(async () => {
        const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard')
        if (isIosNative() && authKeyboardActive) {
            noteNativeResizeMode('none')
            await Keyboard.setResizeMode({ mode: KeyboardResize.None })
            await Keyboard.setScroll({ isDisabled: true })
            // The plugin hides the bar above the keyboard by default, which
            // also hides Password AutoFill and email suggestions.
            await Keyboard.setAccessoryBarVisible({ isVisible: true })
            await setStatusBarOverlay()
            return
        }
        if (isIosNative() && (chatKeyboardActive || sheetKeyboardActive)) {
            noteNativeResizeMode('none')
            await Keyboard.setResizeMode({ mode: KeyboardResize.None })
            await Keyboard.setScroll({ isDisabled: true })
            await Keyboard.setAccessoryBarVisible({ isVisible: false })
            await setStatusBarOverlay()
            return
        }
        noteNativeResizeMode('native')
        await Keyboard.setResizeMode({ mode: KeyboardResize.Native })
        if (isIosNative()) {
            await Keyboard.setScroll({ isDisabled: false })
            await Keyboard.setAccessoryBarVisible({ isVisible: false })
            await setStatusBarOverlay()
        }
    }).catch(() => {
        // Keyboard plugin missing, or this platform has no resize mode.
    })

    applyQueue = job
    return job
}

export async function startAuthKeyboard(screen: HTMLElement | null) {
    if (!isIosNative()) return

    const token = ++startToken
    authKeyboardActive = true
    screenEl = screen
    noteBaseHeight()
    overlayViewport()
    paintCanvas(screen)

    await applyNativeKeyboardMode()
    if (token !== startToken || !authKeyboardActive) return
    // Overlay grows the WebView under the status bar. Measure after that,
    // not the shorter height from when the solid bar was still reserved.
    noteBaseHeight()
    // Numeric `top` so the first slide interpolates instead of jumping from `auto`.
    const stage = stageEl()
    if (stage) stage.style.top = '0px'

    window.addEventListener('resize', onViewportChange)
    window.visualViewport?.addEventListener('resize', onViewportChange)

    const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard')
    const show = await Keyboard.addListener('keyboardWillShow', (info) => {
        // The native snap is scheduled ~keyboard-duration later. Switching to
        // none now makes that snap a no-op, so the WebView stays full screen.
        noteNativeResizeMode('none')
        void Keyboard.setResizeMode({ mode: KeyboardResize.None })
        paintCanvas(screenEl)
        lastKeyboardHeight = info.keyboardHeight || 0
        applyShift(lastKeyboardHeight)
    })
    const hide = await Keyboard.addListener('keyboardWillHide', () => {
        lastKeyboardHeight = 0
        applyShift(0)
    })

    if (token !== startToken) {
        await show.remove()
        await hide.remove()
        return
    }
    listeners = [show, hide]
}

export async function stopAuthKeyboard() {
    if (!isIosNative()) return

    startToken += 1
    authKeyboardActive = false
    lastKeyboardHeight = 0
    window.removeEventListener('resize', onViewportChange)
    window.visualViewport?.removeEventListener('resize', onViewportChange)
    const stage = stageEl()
    window.clearTimeout(caretHoldTimer)
    document.documentElement.classList.remove('native-auth-caret-hold')
    if (stage) {
        stage.style.transform = ''
        stage.style.top = ''
        stage.style.height = ''
        stage.style.minHeight = ''
    }
    restoreCanvas()
    restoreViewport()
    screenEl = null

    const pending = listeners
    listeners = []
    await Promise.all(pending.map((listener) => listener.remove()))
    try {
        const { Keyboard } = await import('@capacitor/keyboard')
        await Keyboard.hide()
    } catch {
        // Already hidden, or the plugin is unavailable.
    }
    await applyNativeKeyboardMode()
}
